/**
 * Utility functions for strategist agent breakdown hierarchy management
 */

/**
 * Parse AC text based on format
 * @param {string} text - The raw AC text
 * @param {string} format - The format type ('given-when-then', 'bullet', 'checklist')
 * @returns {Array} - Parsed criteria array
 */
export const parseACText = (text, format) => {
  if (!text) return [];
  
  if (format === 'given-when-then') {
    // Split by numbered items first
    const numberedSections = text.split(/\d+\)\s*/).filter(section => section.trim());
    const criteria = [];
    
    numberedSections.forEach(section => {
      const lines = section.split('\n').filter(line => line.trim());
      let currentCriterion = {};
      
      lines.forEach(line => {
        const trimmedLine = line.trim();
        if (trimmedLine.toLowerCase().startsWith('given')) {
          currentCriterion.given = trimmedLine.replace(/^given\s+/i, '');
        } else if (trimmedLine.toLowerCase().startsWith('when')) {
          currentCriterion.when = trimmedLine.replace(/^when\s+/i, '');
        } else if (trimmedLine.toLowerCase().startsWith('then')) {
          currentCriterion.then = trimmedLine.replace(/^then\s+/i, '');
        }
      });
      
      if (currentCriterion.given || currentCriterion.when || currentCriterion.then) {
        criteria.push({
          given: currentCriterion.given || '',
          when: currentCriterion.when || '',
          then: currentCriterion.then || ''
        });
      }
    });
    
    return criteria.length > 0 ? criteria : [{ given: text, when: '', then: '' }];
  }
  
  // For checklist or bullet format, split by lines or numbered items
  return text.split('\n')
    .filter(line => line.trim())
    .map(line => {
      let cleanLine = line.replace(/^\d+\)\s*/, '').trim();
      // Remove bullet points for bullet format
      if (format === 'bullet') {
        cleanLine = cleanLine.replace(/^[•·▪▫‣⁃]\s*/, '').trim();
      }
      // Remove checkbox indicators for checklist format
      if (format === 'checklist') {
        cleanLine = cleanLine.replace(/^\[\s*\]\s*/, '').trim();
      }
      return cleanLine;
    })
    .filter(line => line.length > 0); // Remove empty lines after cleaning
};

/**
 * Determines the node type based on its position in the hierarchy
 * @param {Object} node - The breakdown node
 * @param {number} level - Current nesting level (0 = root)
 * @param {string|null} parentType - Type of parent node
 * @param {string} context - Context: 'root-epic', 'root-features', 'root-tasks', etc.
 * @returns {string} - Node type: 'epic', 'feature', 'task', or 'subtask'
 */
export const getBreakdownNodeType = (node, level, parentType, context = null) => {
  // Root level detection
  if (level === 0) {
    if (context === 'root-epic') return 'epic';
    if (context === 'root-features') return 'feature';
    if (context === 'root-tasks') return 'task';
    if (context === 'root-subtasks') return 'subtask';
    
    // Fallback for root level without context
    if (node.features && node.features.length > 0) return 'epic';
    if (node.tasks && node.tasks.length > 0 && !node.features) return 'feature';
    return 'task';
  }
  
  // Child level detection based on parent type
  if (parentType === 'epic') {
    // Epic can contain features or tasks directly
    if (node.tasks && node.tasks.length > 0) return 'feature';
    return 'feature';
  }
  
  if (parentType === 'feature') {
    return 'task';
  }
  
  if (parentType === 'task') {
    return 'subtask';
  }
  
  // Default fallback
  return 'subtask';
};

/**
 * Renders breakdown items with proper hierarchy
 * @param {Object} breakdown - The breakdown object
 * @param {Function} renderFunction - Function to render individual items
 * @returns {Array} - Array of rendered items
 */
export const renderBreakdownHierarchy = (breakdown, renderFunction) => {
  const items = [];
  
  if (breakdown.epic) {
    items.push(renderFunction(breakdown.epic, 0, null, 0, 'root-epic'));
  }
  
  if (breakdown.features) {
    breakdown.features.forEach((feature, idx) => {
      items.push(renderFunction(feature, 0, null, idx, 'root-features'));
    });
  }
  
  if (breakdown.tasks) {
    breakdown.tasks.forEach((task, idx) => {
      items.push(renderFunction(task, 0, null, idx, 'root-tasks'));
    });
  }
  
  if (breakdown.subtasks) {
    breakdown.subtasks.forEach((subtask, idx) => {
      items.push(renderFunction(subtask, 0, null, idx, 'root-subtasks'));
    });
  }
  
  return items;
};